import React, { useState } from 'react';
import { Tooltip, Tag, Button } from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    CustomerServiceOutlined,
    MedicineBoxOutlined,
    TeamOutlined,
    UserOutlined
} from '@ant-design/icons';
import moment from 'moment';

import DropdownActions from './DropdownActions';
import PhoneNumberList from '@/components/PhoneNumberList';
import TextTruncate from 'react-text-truncate';
import HtmlParser from 'react-html-parser';
import {
    isValidDate,
    renderAppointmentName,
    renderCustomerServiceStatus,
    stripHtml
} from '@/utils/helpers';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import RevenueSummaryCell from '@/components/RevenueSummaryCell';
import CreatedAtCell from '@/components/CreatedAtCell';
import BirthdayCell from './BirthdayCell';
import { checkPermission } from '@/utils/permission';
import { getCustomerServiceDoctorRoleGroups } from '@/components/customer-services/customerServiceDoctorRoleHelpers';

/** utils nhỏ */
const mergeCol = (base, patch) => ({ ...base, ...patch });

const insertExtras = (cols, extras = []) => {
    const out = [...cols];

    for (const ex of extras) {
        if (!ex?.key || !ex?.column) continue;

        const exists = out.findIndex(c => c?.key === ex.key);
        if (exists >= 0) out.splice(exists, 1);

        const { insert = {} } = ex;
        const { beforeKey, afterKey, atIndex } = insert;

        if (typeof atIndex === 'number') {
            out.splice(Math.max(0, Math.min(atIndex, out.length)), 0, ex.column);
        } else if (beforeKey) {
            const idx = out.findIndex(c => c?.key === beforeKey);
            out.splice(idx >= 0 ? idx : out.length, 0, ex.column);
        } else if (afterKey) {
            const idx = out.findIndex(c => c?.key === afterKey);
            out.splice(idx >= 0 ? idx + 1 : out.length, 0, ex.column);
        } else {
            out.push(ex.column);
        }
    }

    return out;
};

/**
 * Tạo danh sách cột cho bảng khách hàng + hỗ trợ tuỳ biến ẩn/hiện/override/chèn thêm
 *
 * @param {Object} ctx
 *  - onOpenScheduleDetail?: (record)=>void
 *  - loadData?: Function
 *  - showCardNumber?: boolean
 *
 * @param {Object} options
 *  - visibleKeys?: string[]
 *  - hiddenKeys?: string[]
 *  - overrides?: { [key]: Partial<Column> }
 *  - extras?: { key:string, column:Object, insert?:{ beforeKey?:string, afterKey?:string, atIndex?:number } }[]
 *
 * @returns {Array}
 */
export function buildCustomerColumns(ctx = {}, options = {}) {
    const { onOpenScheduleDetail, loadData, showCardNumber } = ctx;

    const {
        visibleKeys,
        hiddenKeys = [],
        overrides = {},
        extras = [],
    } = options;

    const serviceTitleStyle = {
        fontSize: 13,
        fontWeight: 600,
        color: '#262626'
    };

    const statusStyle = {
        fontSize: 12,
        fontWeight: 400,
        color: '#8c8c8c'
    };

    const metaStyle = {
        fontSize: 12,
        color: '#8c8c8c',
        marginBottom: 4
    };

    const sectionLabelStyle = {
        fontWeight: 600,
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        color: '#595959',
        marginRight: 4
    };

    const rolePrefixStyle = {
        fontWeight: 400,
        color: '#8c8c8c'
    };

    const staffNameStyle = {
        fontWeight: 600,
        color: '#111',
        fontSize: 14
    };

    const labelIconStyle = {
        fontSize: 14,
        color: '#8c8c8c',
        marginRight: 6
    };

    const canViewFinance = checkPermission('customer', 'view_finance');

    const CustomerCareCell = ({ logs = [], limit = 1 }) => {
        const [expanded, setExpanded] = useState(false);

        if (!Array.isArray(logs) || logs.length === 0) {
            return <span style={{ color: '#bfbfbf' }}>—</span>;
        }

        const sortedLogs = [...logs].sort(
            (a, b) => new Date(b.called_at).getTime() - new Date(a.called_at).getTime()
        );

        const visibleLogs = expanded ? sortedLogs : sortedLogs.slice(0, limit);

        return (
            <div style={{ lineHeight: 1.5 }}>
                {visibleLogs.map((log, index) => (
                    <div
                        key={log.id || index}
                        style={{
                            padding: '0 0 0 8px',
                            borderLeft: '3px solid #c7e6ff',
                            marginLeft: '-8px',
                            borderBottom:
                                index < visibleLogs.length - 1
                                    ? '3px dashed #c7e6ff'
                                    : 'none'
                        }}
                    >
                        <div style={{ marginBottom: 4 }}>
                            {HtmlParser(log.customer_feedback || '')}
                        </div>

                        {Array.isArray(log.categories) && log.categories.length > 0 && (
                            <div style={{ marginBottom: 4 }}>
                                {log.categories.map(cat => (
                                    <Tag
                                        key={cat.id}
                                        color="blue"
                                        style={{
                                            background: '#f0f5ff',
                                            color: '#2f54eb',
                                            border: '1px solid #adc6ff',
                                            marginBottom: 4,
                                        }}
                                    >
                                        {cat.name}
                                    </Tag>
                                ))}
                            </div>
                        )}

                        <div
                            style={{
                                fontSize: 13,
                                color: '#8c8c8c',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                flexWrap: 'wrap',
                            }}
                        >
                            <span>
                                <UserOutlined /> {log.staff?.full_name || '—'}
                            </span>
                            <span>
                                <ClockCircleOutlined /> {isValidDate(log.called_at, true)}
                            </span>
                        </div>
                    </div>
                ))}

                {sortedLogs.length > limit && (
                    <div>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, fontSize: 13 }}
                            onClick={() => setExpanded(prev => !prev)}
                        >
                            <span style={{ color: 'blue' }}>
                                {expanded ? 'Thu gọn' : `Xem thêm (${sortedLogs.length - limit})`}
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const uniq = (arr = []) => [...new Set(arr.filter(Boolean))];

    const extractStaffNames = (items = [], staffKey) => {
        return uniq((items || []).map(x => x?.[staffKey]?.full_name).filter(Boolean));
    };

    const groupStaffByRole = (items = [], staffKey) => {
        const map = new Map();

        (items || []).forEach(x => {
            const staff = x?.[staffKey];
            if (!staff?.full_name) return;

            const roleName = staff?.user_role?.name || 'Khác';
            if (!map.has(roleName)) map.set(roleName, new Set());
            map.get(roleName).add(staff.full_name);
        });

        return [...map.entries()].map(([role, set]) => ({
            role,
            names: [...set],
        }));
    };

    const ServiceColumnCell = ({ services = [], limit = 2 }) => {
        const [expanded, setExpanded] = useState(false);

        const list = Array.isArray(services) ? services : [];
        if (!list.length) return <span style={{ color: '#bfbfbf' }}>—</span>;

        const sorted = [...list].sort((a, b) => {
            const ta = new Date(a?.selected_at || a?.created_at || 0).getTime();
            const tb = new Date(b?.selected_at || b?.created_at || 0).getTime();
            return tb - ta;
        });

        const visible = expanded ? sorted : sorted.slice(0, limit);

        return (
            <div style={{ lineHeight: 1.6 }}>
                {visible.map((sv, index) => {
                    const serviceName = sv?.service?.name || '—';
                    const statusLabel = renderCustomerServiceStatus(sv?.status) || '—';

                    const selectedAt = sv?.selected_at
                        ? isValidDate(sv.selected_at, false)
                        : (sv?.created_at ? isValidDate(sv.created_at, false) : '—');

                    const doctorRoleGroups = getCustomerServiceDoctorRoleGroups(sv);
                    const assistantNames = extractStaffNames(sv?.assistants, 'assistant');
                    const salesGroups = groupStaffByRole(sv?.sales, 'sales');

                    return (
                        <div
                            key={sv?.id || index}
                            style={{
                                padding: '0 0 0 8px',
                                borderLeft: '3px solid #c7e6ff',
                                marginLeft: '-8px',
                                borderBottom: index < visible.length - 1 ? '3px dashed #c7e6ff' : 'none',
                            }}
                        >
                            <div style={serviceTitleStyle}>
                                {serviceName}{' '}
                                <span style={statusStyle}>· {statusLabel}</span>
                            </div>

                            <div style={metaStyle}>
                                <ClockCircleOutlined style={labelIconStyle} />
                                Ngày chốt: {selectedAt}
                            </div>

                            {doctorRoleGroups.length ? doctorRoleGroups.map(group => (
                                <div key={group.key}>
                                    <MedicineBoxOutlined style={labelIconStyle} />
                                    <span style={sectionLabelStyle}>{group.label}:</span>
                                    <span style={staffNameStyle}>{group.people.map(person => person.name).join(', ')}</span>
                                </div>
                            )) : <div><MedicineBoxOutlined style={labelIconStyle} /><span style={sectionLabelStyle}>Bác sĩ điều trị:</span><span style={staffNameStyle}>—</span></div>}

                            <div>
                                <TeamOutlined style={labelIconStyle} />
                                <span style={sectionLabelStyle}>Phụ tá:</span>
                                <span style={staffNameStyle}>
                                    {assistantNames.length ? assistantNames.join(', ') : '—'}
                                </span>
                            </div>

                            {salesGroups.length > 0 && (
                                <div>
                                    <CustomerServiceOutlined style={labelIconStyle} />
                                    <span style={sectionLabelStyle}>Sales:</span>{' '}
                                    <span>
                                        {salesGroups.map((g, idx) => (
                                            <span key={g.role}>
                                                <span style={rolePrefixStyle}>{g.role}:</span>{' '}
                                                <span style={staffNameStyle}>{g.names.join(', ')}</span>
                                                {idx < salesGroups.length - 1 && '; '}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}

                {sorted.length > limit && (
                    <div>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, fontSize: 13 }}
                            onClick={() => setExpanded(prev => !prev)}
                        >
                            <span style={{ color: 'blue' }}>
                                {expanded ? 'Thu gọn' : `Xem thêm (${sorted.length - limit})`}
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const getNonEmptyNames = (items, getName) => (
        (items || [])
            .map(getName)
            .filter(Boolean)
    );

    const normalizeTreatmentSessions = (treatmentSessions) => {
        if (!treatmentSessions) return [];
        return Array.isArray(treatmentSessions) ? treatmentSessions : [treatmentSessions];
    };

    const treatmentStyles = {
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            lineHeight: 1.5,
            fontSize: 14,
            color: '#262626',
        },
        session: {
            padding: '6px 0',
            borderBottom: '1px solid #f0f0f0',
        },
        doctorRole: {
            color: '#999',
            fontSize: 14,
        },
        doctorName: {
            fontSize: 14,
            fontWeight: 700,
        },
        summaryLine: {
            marginTop: 5,
        },
        summaryLabel: {
            color: '#595959',
            fontWeight: 600,
            fontSize: 14,
            marginRight: 4,
        },
        summaryText: {
            color: '#262626',
            fontSize: 14,
        },
        tags: {
            display: 'inline-flex',
            flexWrap: 'wrap',
            gap: 4,
            verticalAlign: 'top',
        },
        itemTag: {
            margin: 0,
            whiteSpace: 'normal',
            lineHeight: 1.45,
            fontSize: 13,
            border: 0
        },
        empty: {
            color: '#bfbfbf',
            fontSize: 14,
        },
    };

    const renderTreatmentDoctor = (doctor) => {
        if (!doctor?.full_name) return <span style={treatmentStyles.empty}>----</span>;

        const roleName = doctor.user_rolename || doctor.user_role?.name || '';

        return (
            <div>
                {roleName && (
                    <span style={treatmentStyles.doctorRole}>
                        {roleName} -{' '}
                    </span>
                )}
                <span style={treatmentStyles.doctorName}>{doctor.full_name}</span>
            </div>
        );
    };

    const renderCompactLine = (label, items) => (
        items.length ? (
            <div style={treatmentStyles.summaryLine}>
                <span style={treatmentStyles.summaryLabel}>{items.length} {label}:</span>
                <span style={treatmentStyles.tags}>
                    {items.map((item, index) => (
                        <Tag key={`${item}-${index}`} color="blue" style={treatmentStyles.itemTag}>
                            {item}
                        </Tag>
                    ))}
                </span>
            </div>
        ) : null
    );

    const renderTextLine = (label, value) => (
        value ? (
            <div style={treatmentStyles.summaryLine}>
                <span style={treatmentStyles.summaryLabel}>{label}:</span>
                <span style={treatmentStyles.summaryText}>{value}</span>
            </div>
        ) : null
    );

    const renderTreatmentTicket = (record) => {
        const treatmentSessions = normalizeTreatmentSessions(
            record?.treatment_sessions || record?.treatmentSessions
        );

        if (!treatmentSessions.length) {
            return <span style={treatmentStyles.empty}>----</span>;
        }

        return (
            <div style={treatmentStyles.wrapper}>
                {treatmentSessions.map((session, index) => {
                    const procedures = getNonEmptyNames(
                        session?.procedures,
                        (procedure) => procedure?.name
                    );
                    const services = getNonEmptyNames(
                        session?.customer_services || session?.customerServices,
                        (item) => item?.customer_service?.service?.name || item?.customerService?.service?.name
                    );
                    const note = session?.ghi_chu_noi_bo;
                    const treatmentDate = isValidDate(session?.ngay, false);
                    const hasContent = treatmentDate || note || session?.doctor?.full_name || procedures.length || services.length;

                    return (
                        <div
                            key={session?.id || index}
                            style={{
                                ...treatmentStyles.session,
                                borderBottom: index === treatmentSessions.length - 1 ? 0 : treatmentStyles.session.borderBottom,
                            }}
                        >
                            {hasContent ? (
                                <>
                                    {renderTextLine('Ngày điều trị', treatmentDate)}
                                    {session?.doctor?.full_name && renderTreatmentDoctor(session?.doctor)}
                                    {renderCompactLine('thủ thuật', procedures)}
                                    {renderCompactLine('dịch vụ', services)}
                                    {note && (
                                        <div style={treatmentStyles.summaryLine}>
                                            <span style={treatmentStyles.summaryLabel}>Ghi chú:</span>
                                            <span style={treatmentStyles.summaryText}>{HtmlParser(note)}</span>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <span style={treatmentStyles.empty}>----</span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const base = [
        {
            title: '#',
            align: 'center',
            width: '50px',
            dataIndex: 'actions',
            key: 'actions',
            render: (_, record) => <DropdownActions record={record} loadData={loadData} />,
        },
        {
            title: 'Họ và tên',
            dataIndex: 'full_name',
            key: 'full_name',
            width: '250px',
            render: (_, record) => {
                const customer = {
                    id: record?.id,
                    code: record?.code,
                    full_name: record?.full_name,
                    branch_name: record?.branch?.name,
                    conditions: record?.condition_names_string,
                    customer_source: record?.customer_source?.name,
                    customer_status: record?.customer_status?.name,
                    customer_status_color: record?.customer_status?.text_color,
                    medical_book_number: record?.medical_book_number,
                    card_number: record?.card_number_new
                };

                return (
                    <CustomerInfoColumn
                        record={customer}
                        nameField="full_name"
                        idField="id"
                        codeField="code"
                        branchField="branch_name"
                        medicalBookField="medical_book_number"
                        conditionField="conditions"
                        sourceField="customer_source"
                        statusField="customer_status"
                        statusColorField="customer_status_color"
                        cardNumberField="card_number"
                        showCardNumber={showCardNumber}
                    />
                );
            }
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'birthday',
            key: 'birthday',
            width: 120,
            render: (_, record) => (
                <BirthdayCell birthday={record?.birthday} age={record?.age} />
            ),
        },
        {
            title: 'Nickname',
            dataIndex: 'fb_name',
            key: 'fb_name',
            width: '180px',
            render: (_, record) => <>{record?.fb_name || '—'}</>,
        },
        {
            title: 'Số điện thoại',
            dataIndex: 'phone',
            key: 'phone',
            width: '120px',
            render: (_, record) => <PhoneNumberList phones={record?.phones} />,
        },
        {
            title: 'Lịch hẹn gần nhất',
            dataIndex: 'appointments',
            key: 'appointments',
            width: '260px',
            render: (appointments, record) => {
                if (!Array.isArray(appointments) || !appointments.length) return null;

                const sortedAppointments = [...appointments].sort((a, b) => {
                    const timeA = new Date(a?.scheduled_at || 0).getTime();
                    const timeB = new Date(b?.scheduled_at || 0).getTime();
                    return timeB - timeA;
                });

                const firstAppointment = sortedAppointments[0];
                const remainCount = sortedAppointments.length - 1;
                const scheduleTime = firstAppointment?.scheduled_at;
                const note = firstAppointment?.note || '';
                const doctors_text = firstAppointment?.doctors_text || '';

                return (
                    <div>
                        <div
                            style={{
                                lineHeight: 1.6,
                                fontSize: 14,
                                padding: '8px 0 8px 8px',
                                borderLeft: '3px solid #e6f4ff',
                                marginLeft: '-8px',
                            }}
                        >
                            <div style={{ marginBottom: 4 }}>
                                <CalendarOutlined style={{ color: '#1677ff', marginRight: 6 }} />
                                <span style={{ fontWeight: 600, color: '#1677ff' }}>
                                    {scheduleTime ? moment(scheduleTime).format('DD/MM/YYYY HH:mm') : '-'}
                                </span>
                            </div>

                            {/* Bác sĩ */}
                            {!!doctors_text && (
                                <div>
                                    <span style={{ fontWeight: 500 }}>Bác sĩ:</span>{' '}
                                    <span style={{ ...staffNameStyle, color: '#888' }}>
                                        {doctors_text}
                                    </span>
                                </div>
                            )}

                            <div style={{ marginBottom: 4 }}>
                                <span style={{ fontWeight: 500 }}>Nội dung:</span>{' '}
                                <span style={{ color: '#222' }}>
                                    {renderAppointmentName(firstAppointment?.name)}
                                </span>
                            </div>

                            {note && (
                                <div>
                                    <span style={{ fontWeight: 500 }}>Ghi chú:</span>{' '}
                                    <span style={{ color: '#888' }}>{note}</span>
                                </div>
                            )}
                        </div>

                        {remainCount > 0 && (
                            <div style={{ marginTop: 2 }}>
                                <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0, fontSize: 13 }}
                                    onClick={() => onOpenScheduleDetail?.(record)}
                                >
                                    <span style={{ color: 'blue' }}>
                                        Xem thêm ({remainCount}) lịch
                                    </span>
                                </Button>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Phiếu điều trị',
            dataIndex: 'treatment_note',
            key: 'treatment_note',
            width: '350px',
            render: (_, record) => renderTreatmentTicket(record)
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            width: '250px',
            render: (text) => (
                <div style={{ wordBreak: 'break-word' }}>
                    <Tooltip title={HtmlParser(text || '')} placement="topLeft">
                        <TextTruncate line={5} truncateText="…" text={stripHtml(text || '')} />
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Kết quả CSKH',
            key: 'customer_care_logs',
            dataIndex: 'customer_care_logs',
            width: 420,
            render: (logs) => <CustomerCareCell logs={logs} limit={1} />
        },
        {
            key: 'service_info',
            title: 'Dịch vụ',
            width: 390,
            render: (_, record) => (
                <ServiceColumnCell services={record?.active_purchased_services} limit={1} />
            )
        },

        ...(canViewFinance
            ? [
                {
                    title: 'Doanh số & công nợ',
                    key: 'revenue_summary',
                    width: 220,
                    sorter: true,
                    align: 'right',
                    render: (_, record) => (
                        <RevenueSummaryCell
                            financialOverview={record?.financial_overview}
                            align="right"
                        />
                    )
                }
            ]
            : []),

        {
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '220px',
            render: (text, record) => (
                <CreatedAtCell
                    createdAt={text}
                    user={record?.created_user}
                />
            )
        }
    ];

    const merged = base.map(col =>
        col?.key && overrides[col.key]
            ? mergeCol(col, overrides[col.key])
            : col
    );

    const withExtras = insertExtras(merged, extras);

    const allKeys = withExtras.map(c => c?.key).filter(Boolean);

    let selectedKeys = allKeys;

    if (Array.isArray(visibleKeys) && visibleKeys.length) {
        selectedKeys = visibleKeys.filter(k => allKeys.includes(k));
    } else if (Array.isArray(hiddenKeys) && hiddenKeys.length) {
        selectedKeys = allKeys.filter(k => !hiddenKeys.includes(k));
    }

    return withExtras.filter(c => selectedKeys.includes(c?.key));
}
